import { Cancel01Icon, Menu01Icon, TestTube01Icon } from '@hugeicons/core-free-icons';
import { Dialog as DialogPrimitive, Slot } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Icon } from '../primitives/icon.js';

interface ShellContextValue {
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
}
const ShellContext = React.createContext<ShellContextValue | null>(null);

/** Access the shell's mobile navigation state (e.g. to close it after navigating). */
export function useAppShell(): ShellContextValue | null {
  return React.useContext(ShellContext);
}

export interface AppShellProps {
  /** Logo / product name at the top of the sidebar. */
  brand: React.ReactNode;
  /** Navigation content: NavGroup / NavItem elements (the selected module's sections when a rail is used). */
  nav: React.ReactNode;
  /** Optional module rail (ModuleRail) shown as a thin column left of the sidebar. */
  rail?: React.ReactNode;
  /** Heading above the section nav (e.g. the selected module's name). */
  navTitle?: React.ReactNode;
  /** Bottom of the sidebar (user menu, help). */
  sidebarFooter?: React.ReactNode;
  /** Top header content: workspace switcher, project filter, search, notifications. */
  header?: React.ReactNode;
  /** Full-width banner above the header (e.g. <ModeBanner mode="test" />). */
  banner?: React.ReactNode;
  children: React.ReactNode;
  /** Label for the navigation landmark. */
  navLabel?: string;
  className?: string;
}

/**
 * Application frame: fixed left sidebar on ≥1024px; below that the sidebar becomes a modal sheet opened
 * from the header's menu button. Includes a skip link to the main content.
 */
export function AppShell({
  brand,
  nav,
  rail,
  navTitle,
  sidebarFooter,
  header,
  banner,
  children,
  navLabel = 'Navegación principal',
  className,
}: AppShellProps) {
  const [navOpen, setNavOpen] = React.useState(false);
  const ctx = React.useMemo(() => ({ navOpen, setNavOpen }), [navOpen]);
  const sidebar = (
    <div className="flex h-full w-full flex-col">
      <div className="flex h-topbar shrink-0 items-center px-5">{brand}</div>
      {navTitle ? <div className="px-5 pb-1 pt-1 text-meta font-semibold uppercase tracking-wide text-text-secondary">{navTitle}</div> : null}
      <div className="flex-1 overflow-y-auto px-3 pb-4">{nav}</div>
      {sidebarFooter ? (
        <div className="shrink-0 border-t border-border-subtle p-3">
          {sidebarFooter}
        </div>
      ) : null}
    </div>
  );
  return (
    <ShellContext.Provider value={ctx}>
      <div
        className={cn(
          'min-h-dvh bg-surface-canvas text-text-primary',
          className,
        )}
      >
        <a
          href="#ef-main"
          className="sr-only z-[70] rounded-control bg-surface-default px-4 py-2 text-body font-medium text-brand-primary shadow-overlay focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
        >
          Saltar al contenido
        </a>
        {rail ? (
          <div className="fixed inset-y-0 left-0 z-30 hidden w-rail border-r border-border-subtle bg-surface-subtle lg:flex">
            {rail}
          </div>
        ) : null}
        <nav
          aria-label={navLabel}
          className={cn(
            'fixed inset-y-0 z-30 hidden w-sidebar border-r border-border-subtle bg-surface-default lg:flex',
            rail ? 'left-rail' : 'left-0',
          )}
        >
          {sidebar}
        </nav>
        <DialogPrimitive.Root open={navOpen} onOpenChange={setNavOpen}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in lg:hidden" />
            <DialogPrimitive.Content
              className={cn(
                'fixed inset-y-0 left-0 z-50 flex bg-surface-default shadow-overlay animate-slide-in-left lg:hidden',
                rail ? 'w-[min(360px,92vw)]' : 'w-[min(288px,86vw)]',
              )}
            >
              {rail ? (
                <div
                  className="flex w-rail shrink-0 border-r border-border-subtle bg-surface-subtle"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('a[href]')) setNavOpen(false);
                  }}
                >
                  {rail}
                </div>
              ) : null}
              <DialogPrimitive.Title className="sr-only">
                {navLabel}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Menú de secciones
              </DialogPrimitive.Description>
              <nav
                aria-label={navLabel}
                className="flex min-w-0 flex-1"
                onClick={(e) => {
                  if (
                    (e.target as HTMLElement).closest(
                      'a[href],[data-ef-nav-item]',
                    )
                  )
                    setNavOpen(false);
                }}
              >
                {sidebar}
              </nav>
              <DialogPrimitive.Close
                aria-label="Cerrar menú"
                className="absolute right-2 top-3 inline-flex size-9 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus"
              >
                <Icon icon={Cancel01Icon} className="size-[18px]" />
              </DialogPrimitive.Close>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
        <div className={cn('flex min-h-dvh min-w-0 flex-col', rail ? 'lg:pl-rail-sidebar' : 'lg:pl-sidebar')}>
          {banner}
          <header className="sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-3 border-b border-border-subtle bg-surface-default px-4 md:px-6">
            <button
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Abrir menú"
              aria-expanded={navOpen}
              className="-ml-1 inline-flex size-10 shrink-0 items-center justify-center rounded-control text-text-secondary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus lg:hidden"
            >
              <Icon icon={Menu01Icon} className="size-5" />
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
              {header}
            </div>
          </header>
          <main
            id="ef-main"
            tabIndex={-1}
            className="min-w-0 flex-1 px-4 py-6 outline-none md:px-6 xl:px-8"
          >
            {children}
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

export interface NavGroupProps {
  /** Visible group heading. Omit for the first ungrouped block. */
  label?: string;
  children: React.ReactNode;
  className?: string;
}

export function NavGroup({ label, children, className }: NavGroupProps) {
  const id = React.useId();
  return (
    <div
      className={cn('mt-5 first:mt-1', className)}
      role="group"
      aria-labelledby={label ? id : undefined}
    >
      {label ? (
        <p
          id={id}
          className="px-3 pb-1.5 text-meta font-medium text-text-secondary"
        >
          {label}
        </p>
      ) : null}
      <ul className="grid gap-0.5">{children}</ul>
    </div>
  );
}

export interface NavItemProps {
  label: string;
  icon?: React.ReactNode;
  /** Current page: accent background + bold text + aria-current="page". */
  selected?: boolean;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Count or status at the end (e.g. unread). Include text, not just a dot. */
  badge?: React.ReactNode;
  /** Render the single child (e.g. a router Link) with nav styles instead of an <a>. */
  asChild?: boolean;
  children?: React.ReactElement;
}

export function NavItem({
  label,
  icon,
  selected,
  href,
  onClick,
  badge,
  asChild,
  children,
}: NavItemProps) {
  const className = cn(
    'flex min-h-10 w-full items-center gap-3 rounded-control px-3 py-2 text-body transition-colors duration-[var(--ef-motion-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus [&_svg]:size-[18px] [&_svg]:shrink-0',
    selected
      ? 'bg-accent-selected font-semibold text-accent-on-selected'
      : 'font-medium text-text-secondary hover:bg-surface-subtle hover:text-text-primary',
  );
  const content = (
    <>
      {icon}
      {/* Wraps instead of truncating: the full label stays readable (and measurable for contrast checks). */}
      <span className="min-w-0 flex-1 text-left leading-snug [overflow-wrap:anywhere]">
        {label}
      </span>
      {badge}
    </>
  );
  const common = {
    className,
    'aria-current': selected ? ('page' as const) : undefined,
    'data-ef-nav-item': '',
  };
  return (
    <li>
      {asChild && children ? (
        <Slot.Root {...common} onClick={onClick}>
          {React.cloneElement(children, {}, content)}
        </Slot.Root>
      ) : href ? (
        <a href={href} onClick={onClick} {...common}>
          {content}
        </a>
      ) : (
        <button type="button" onClick={onClick} {...common}>
          {content}
        </button>
      )}
    </li>
  );
}

export interface ModeBannerProps {
  mode: 'test' | 'demo';
  /** Extra explanation. Defaults to a sensible message per mode. */
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/** Persistent banner for test mode / demo data. Text + icon, not color only. */
export function ModeBanner({
  mode,
  children,
  action,
  className,
}: ModeBannerProps) {
  const title = mode === 'test' ? 'Modo de prueba' : 'Datos de demostración';
  const text =
    children ??
    (mode === 'test'
      ? 'Solo se contacta a números de prueba permitidos. Nada se envía a prospectos reales.'
      : 'Estás viendo datos ficticios. Ninguna acción afecta a contactos reales.');
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-subtle bg-warning-bg px-4 py-2 text-body text-warning md:px-6',
        className,
      )}
    >
      <Icon icon={TestTube01Icon} className="size-4 shrink-0" />
      <span className="font-semibold">{title}.</span>
      <span className="min-w-0 flex-1">{text}</span>
      {action}
    </div>
  );
}
